import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Observable, ReplaySubject } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    skip,
    startWith,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';

import isEqual from 'lodash-es/isEqual';
import { BaseContainer } from '../../../../../core/common/containers/base.abstract.container';
import { AutocompleteFunction } from '../../../../../core/common/entities/autocompleteFunction.entity';
import { DateRange } from '../../../../../core/dates/entities/date-range.entity';
import { CreateDateRangeQuery, ParseDateRangeQuery } from '../../../../../core/dates/helpers/date-range-query-helper';
import { TranslateDateRangeQuery } from '../../../../../core/dates/helpers/date-range-query-translation-helper';
import { BaseNestedFieldDefinition } from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { DroppanelStyle } from '../../../../../elements/components/droppanel/entities/droppanel-style.entity';

import { ShFormInputWithDroppanelComponent } from '../../../../../form-controls/components/_core/form-input-with-droppanel/form-input-with-droppanel.component';
import { ShFormLabelComponent } from '../../../../../form-controls/components/_core/form-label/form-label.component';
import { ShFormDateSelectComponent } from '../../../../../form-controls/components/form-date-select/form-date-select.component';
import { ShFormMultiSelectComponent } from '../../../../../form-controls/components/form-multi-select/main/form-multi-select.component';
import { ShFormNumberBaseComponent } from '../../../../../form-controls/components/form-number/base-simple/form-number-base.component';
import { ShFormTextBaseComponent } from '../../../../../form-controls/components/form-text/base-simple/form-text-base.component';
import { FormInputWithDroppanelVariant } from '../../../../../form-controls/entities/form-input-with-droppanel-variant.entity';
import { MultiFormSelectOptions } from '../../../../../form-controls/entities/multi-form-select-options.entity';
import { AdvancedSearchDialogTranslations } from '../../../entities/i18n/advanced-search-translations.entity';
import { ValueType } from '../../../entities/valueType.entity';
import { BaseAdvancedSearchSandbox } from '../../../sandbox/advanced-search.sandbox';

@Component({
    standalone: true,
    selector: 'sh-advanced-search-row-value',
    templateUrl: './advanced-search-row-value.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShFormLabelComponent,
        ShFormTextBaseComponent,
        ShFormMultiSelectComponent,
        ShFormInputWithDroppanelComponent,
        ShFormDateSelectComponent,
        ShFormNumberBaseComponent
    ]
})
export class ShAdvancedSearchRowValueComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly profileField$: ReplaySubject<ExtendedBaseProfileField<BaseNestedFieldDefinition> | null> =
        new ReplaySubject<ExtendedBaseProfileField<BaseNestedFieldDefinition> | null>(1);

    public pureProfileField$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>>;
    public valueType$!: Observable<ValueType>;
    public autocompleteFunction$?: Observable<AutocompleteFunction | undefined>;

    public readonly ValueType: typeof ValueType = ValueType;

    @Input() id!: string;
    @Input() i18n!: AdvancedSearchDialogTranslations;
    @Input() label!: string;

    @Input()
    set profileField(profileField: ExtendedBaseProfileField<BaseNestedFieldDefinition> | null) {
        this.profileField$.next(profileField);
    }

    @Input() fieldDefinitionOptions!: MultiFormSelectOptions;
    @Input() control!: UntypedFormControl;
    @Input() isHeader = false;
    @Input() searchPlaceholder!: string;

    @Output() searchChanges: EventEmitter<string> = new EventEmitter<string>();

    public dateLabel$!: Observable<string>;
    public internalControl: UntypedFormControl = new UntypedFormControl();
    public dateRangeControl!: UntypedFormGroup;
    public variant: FormInputWithDroppanelVariant = FormInputWithDroppanelVariant.Default;
    public droppanelStyle: DroppanelStyle = DroppanelStyle.Default;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly datePipe: DatePipe,
        private readonly sandbox: BaseAdvancedSearchSandbox<BaseNestedFieldDefinition>
    ) {
        super();
    }

    ngOnInit(): void {
        this.initFieldDefinitionStream();
        this.initValueTypeStream();
        this.initDateRangeForm();
        this.initFormListeners();
        this.initDateLabelListener();
        this.initAutocompleteFunctionStream();
    }

    public onSearchTermChanged(value: string): void {
        this.searchChanges.emit(value);
    }

    private initFieldDefinitionStream(): void {
        this.pureProfileField$ = this.profileField$.pipe(
            filter(
                (
                    profileField: ExtendedBaseProfileField<BaseNestedFieldDefinition> | null
                ): profileField is ExtendedBaseProfileField<BaseNestedFieldDefinition> => !!profileField
            )
        );
    }

    private initValueTypeStream(): void {
        this.valueType$ = this.pureProfileField$.pipe(
            map(profileField => this.sandbox.getValueType(profileField)),
            startWith(ValueType.LongText),
            shareReplay(1)
        );
    }

    private initDateRangeForm(): void {
        this.dateRangeControl = this.formBuilder.group({
            From: this.formBuilder.group({
                Year: [null, []],
                Month: [null, []],
                Day: [null, []]
            }),
            Until: this.formBuilder.group({
                Year: [null, []],
                Month: [null, []],
                Day: [null, []]
            })
        });
    }

    private initFormListeners(): void {
        this.valueType$
            .pipe(takeUntil(this.destroy$))
            .subscribe(valueType => this.handleExternalChange(this.control.value, valueType));

        this.profileField$.pipe(takeUntil(this.destroy$), skip(1)).subscribe(valueType => this.clearForms());

        this.internalControl.valueChanges
            .pipe(
                withLatestFrom(this.valueType$),
                distinctUntilChanged(isEqual),
                map(([value, valueType]) => this.mapValueToString(value, valueType)),
                takeUntil(this.destroy$)
            )
            .subscribe(value => this.control.setValue(value));

        this.dateRangeControl.valueChanges
            .pipe(
                withLatestFrom(this.valueType$),
                filter(([value, valueType]) => valueType === ValueType.Date),
                map(([value, valueType]) => value),
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.propagateDateFilter());
    }

    private initDateLabelListener(): void {
        this.dateLabel$ = this.dateRangeControl.valueChanges.pipe(
            map(value => this.getValueFromDateForm()),
            startWith(this.control.value),
            withLatestFrom(this.valueType$),
            map(([value, valueType]) =>
                valueType === ValueType.Date
                    ? TranslateDateRangeQuery(value, this.i18n.value.date.from, this.i18n.value.date.to, this.datePipe)
                    : ''
            )
        );
    }

    private initAutocompleteFunctionStream(): void {
        this.autocompleteFunction$ = this.pureProfileField$.pipe(
            map(profileField => this.sandbox.getAutocompleteFct(profileField.FieldDefinition.DottedKey))
        );
    }

    private clearForms(): void {
        this.internalControl.setValue(null);
        this.setValueToDateForm(null);
    }

    private handleExternalChange(value: string | null, valueType: ValueType): void {
        if (!value) {
            this.clearForms();
        }

        if (valueType === ValueType.Date) {
            this.setValueToDateForm(value);
        } else {
            const parsedValue: string | string[] | null = this.mapStringToValue(value, valueType);

            this.internalControl.setValue(parsedValue);
        }
    }

    private propagateDateFilter(): void {
        const range: string = this.getValueFromDateForm();
        this.control.setValue(range);
    }

    private mapValueToString(value: string | number | string[] | null, valueType: ValueType): string | null {
        if (value === null) {
            return null;
        }

        if (valueType === ValueType.Option || valueType === ValueType.Group) {
            const values: string[] = value as string[];

            return values.length > 0 ? values[0] : null;
        } else {
            const stringValue: string = value as string;

            return stringValue.toString() || null;
        }
    }

    private mapStringToValue(value: string | number | null, valueType: ValueType): string | string[] | null {
        if (value === null) {
            return null;
        }

        if (valueType === ValueType.Option || valueType === ValueType.Group) {
            return [value.toString()];
        } else {
            return value.toString();
        }
    }

    private setValueToDateForm(queryPart: string | null): void {
        const dateRange: DateRange = ParseDateRangeQuery(queryPart);

        this.fromYearControl.setValue(dateRange.From.Year);
        this.fromMonthControl.setValue(dateRange.From.Month);
        this.fromDayControl.setValue(dateRange.From.Day);
        this.untilYearControl.setValue(dateRange.Until.Year);
        this.untilMonthControl.setValue(dateRange.Until.Month);
        this.untilDayControl.setValue(dateRange.Until.Day);
    }

    private getValueFromDateForm(): string {
        const dateRange: DateRange = this.dateRangeControl.value;

        return CreateDateRangeQuery(dateRange);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.profileField$.complete();
    }

    // from controls
    get fromControl(): UntypedFormControl {
        return this.dateRangeControl.get('From') as UntypedFormControl;
    }

    get untilControl(): UntypedFormControl {
        return this.dateRangeControl.get('Until') as UntypedFormControl;
    }

    get fromYearControl(): UntypedFormControl {
        return this.fromControl.get('Year') as UntypedFormControl;
    }

    get fromMonthControl(): UntypedFormControl {
        return this.fromControl.get('Month') as UntypedFormControl;
    }

    get fromDayControl(): UntypedFormControl {
        return this.fromControl.get('Day') as UntypedFormControl;
    }

    // until controls
    get untilYearControl(): UntypedFormControl {
        return this.untilControl.get('Year') as UntypedFormControl;
    }

    get untilMonthControl(): UntypedFormControl {
        return this.untilControl.get('Month') as UntypedFormControl;
    }

    get untilDayControl(): UntypedFormControl {
        return this.untilControl.get('Day') as UntypedFormControl;
    }
}
